import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getDriverTripByToken,
  markDriverTrackingByToken,
  saveDriverReadinessGpsByToken,
  saveDriverReadinessPracticeByToken,
  syncDriverLocationsByToken,
} from "../services/tripTrackingApi";
import { TripDetail, applyEvent, usePwaInstallPrompt } from "./DriverPortal";
import { driverEpassFileUrl } from "../services/epassApi";
import { formatDateDMY } from "../utils/dateFormat";
import { EPASS_NOT_NEEDED } from "../utils/epassStatus";
import useDriverGpsTracking from "../hooks/useDriverGpsTracking";
import {
  flushPendingEvents,
  getPendingEvents,
  queueEvent,
  removePendingEvent,
  toOptimisticEvent,
} from "../services/driverOfflineQueue";

const PENDING_SYNC_RETRY_MS = 20000;

export default function DriverBookingPortal() {
  const { token } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [marking, setMarking] = useState("");
  const [readinessBusy, setReadinessBusy] = useState("");

  const scopeKey = `token:${token}`;

  const gpsStatus = useDriverGpsTracking({
    detail,
    scopeKey: `gps:${scopeKey}`,
    sendBatch: useCallback((pings) => syncDriverLocationsByToken(token, { pings }), [token]),
  });
  const installPrompt = usePwaInstallPrompt();

  const syncPending = useCallback(async () => {
    if (!token) return;
    await flushPendingEvents(
      scopeKey,
      (item) =>
        markDriverTrackingByToken(token, {
          eventType: item.eventType,
          placeKey: item.placeKey,
          placeLabel: item.placeLabel,
          clientCapturedAt: item.clientCapturedAt,
          latitude: item.latitude,
          longitude: item.longitude,
          accuracy: item.accuracy,
          gpsProofType: item.gpsProofType,
          gpsCapturedAt: item.gpsCapturedAt,
        }),
      (event) => setDetail((current) => applyEvent(current, event)),
      { captureGpsProof: captureEventLocation }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, scopeKey]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getDriverTripByToken(token);
      const pending = getPendingEvents(scopeKey);
      setDetail(pending.reduce((current, item) => applyEvent(current, toOptimisticEvent(item)), data));
      if (pending.length) syncPending();
    } catch (err) {
      setError(err?.response?.data?.message || "This trip link is invalid or no longer active.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    window.addEventListener("online", syncPending);
    const interval = setInterval(syncPending, PENDING_SYNC_RETRY_MS);
    return () => {
      window.removeEventListener("online", syncPending);
      clearInterval(interval);
    };
  }, [token, syncPending]);

  const mark = async ({ eventType, placeKey, placeLabel }) => {
    const key = `${eventType}:${placeKey}`;
    const location = await captureEventLocation("EXACT");
    if (!hasLocation(location)) {
      toast.error(gpsHelpMessage());
      return;
    }
    const action = { eventType, placeKey, placeLabel, clientCapturedAt: new Date().toISOString(), ...location };

    setDetail((current) => applyEvent(current, toOptimisticEvent(action)));
    queueEvent(scopeKey, action);

    setMarking(key);
    try {
      const event = await markDriverTrackingByToken(token, action);
      removePendingEvent(scopeKey, eventType, placeKey);
      setDetail((current) => applyEvent(current, event));
      toast.success("Timestamp captured");
    } catch (error) {
      if (error?.response) {
        toast.error(error.response?.data?.message || "Could not save timestamp");
      } else {
        toast.info("No connection - saved on this device and will sync automatically once you're back online");
      }
    } finally {
      setMarking("");
    }
  };

  const updateReadiness = (readiness) => {
    setDetail((current) => (current ? { ...current, driverReadiness: readiness } : current));
  };

  const testReadinessGps = async () => {
    setReadinessBusy("gps");
    try {
      const location = await captureEventLocation("EXACT");
      if (!hasLocation(location)) {
        toast.error(gpsHelpMessage());
        return;
      }
      const readiness = await saveDriverReadinessGpsByToken(token, {
        ...location,
        clientCapturedAt: location.gpsCapturedAt || new Date().toISOString(),
        deviceInfo: deviceInfo(),
      });
      updateReadiness(readiness);
      toast.success("GPS test completed");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save GPS test");
    } finally {
      setReadinessBusy("");
    }
  };

  const completeReadinessPractice = async () => {
    setReadinessBusy("practice");
    try {
      const readiness = await saveDriverReadinessPracticeByToken(token, {
        clientCapturedAt: new Date().toISOString(),
        deviceInfo: deviceInfo(),
      });
      updateReadiness(readiness);
      toast.success("Practice checkbox completed");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Complete GPS test first");
    } finally {
      setReadinessBusy("");
    }
  };

  return (
    <Container fluid className="driver-portal-page py-3 px-2 px-sm-3">
      <Row className="driver-portal-header align-items-center g-2 mb-3">
        <Col>
          <h3 className="mb-0 fw-bold"><i className="bi bi-car-front-fill me-2" />Driver Portal</h3>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>Refresh</Button>
        </Col>
      </Row>
      <OpenInDriverApp token={token} />
      {!loading && !error && <DriverEpasses token={token} epasses={detail?.epasses} />}
      <Card className="driver-panel shadow-sm border-0">
        <Card.Body>
          {loading && <div className="py-5 text-center"><Spinner /></div>}
          {!loading && error && <div className="text-danger">{error}</div>}
          {!loading && !error && detail && (
            <TripDetail
              detail={detail}
              selectedTrip={null}
              marking={marking}
              onMark={mark}
              gpsStatus={gpsStatus}
              installPrompt={installPrompt}
              readinessBusy={readinessBusy}
              onReadinessGps={testReadinessGps}
              onReadinessPractice={completeReadinessPractice}
            />
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

// Android opens this link in the driver app automatically once it is installed and the link is
// verified. WhatsApp sometimes keeps the link inside its own browser instead, and the driver is
// then silently back on the web page, where tracking stops the moment the screen locks. This gives
// them a way out that always works.
function OpenInDriverApp({ token }) {
  const isAndroid = typeof navigator !== "undefined" && /android/i.test(navigator.userAgent || "");
  const [dismissed, setDismissed] = useState(false);
  if (!isAndroid || !token || dismissed) return null;
  // An intent URL launches the app if present, and falls back to the Play/website URL if not.
  const intentUrl =
    `intent://www.iotrips.in/driver/booking/${encodeURIComponent(token)}#Intent;` +
    "scheme=https;package=in.iotrips.driver;S.browser_fallback_url=" +
    encodeURIComponent(`https://www.iotrips.in/driver/booking/${token}`) +
    ";end";
  return (
    <Alert variant="primary" className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2">
      <div className="small">
        <strong>Use the Driver app for this trip.</strong>
        <div>Location keeps sending even when your phone is locked.</div>
      </div>
      <div className="d-flex gap-2">
        <Button size="sm" href={intentUrl}>Open in app</Button>
        <Button size="sm" variant="outline-secondary" onClick={() => setDismissed(true)}>Not now</Button>
      </div>
    </Alert>
  );
}

// What the driver shows at the checkpoint. Only what to do and when - applying is the owner's or
// IO Trips' job.
function DriverEpasses({ token, epasses }) {
  const shown = (epasses || []).filter((epass) => epass.status !== EPASS_NOT_NEEDED);
  if (!shown.length) return null;
  return (
    <Card className="driver-panel shadow-sm border-0 mb-3">
      <Card.Body>
        <h5 className="fw-bold mb-2"><i className="bi bi-file-earmark-check me-2" />E-Pass</h5>
        {shown.map((epass) => (
          <div key={epass.epassId} className="border rounded p-2 mb-2">
            <div className="fw-semibold">{epass.name}</div>
            <div className="small text-muted">Show at the checkpoint on {formatDateDMY(epass.neededOn, "-")}</div>
            {epass.fileName ? (
              <div className="d-flex flex-wrap gap-2 mt-2">
                <Button size="sm" href={driverEpassFileUrl(token, epass.epassId)} target="_blank" rel="noreferrer">
                  Open E-Pass
                </Button>
                <Button size="sm" variant="outline-secondary" href={driverEpassFileUrl(token, epass.epassId, true)}>
                  Download
                </Button>
              </div>
            ) : (
              <div className="small text-danger fw-semibold mt-1">Not uploaded yet. Check here again before the trip.</div>
            )}
          </div>
        ))}
      </Card.Body>
    </Card>
  );
}

function captureEventLocation(proofType = "EXACT") {
  if (!navigator.geolocation) return Promise.resolve({ gpsProofType: "MISSING" });
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        gpsProofType: proofType,
        gpsCapturedAt: new Date().toISOString(),
      }),
      () => resolve({ gpsProofType: "MISSING" }),
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 12000 },
    );
  });
}

function hasLocation(location) {
  return location?.latitude != null && location?.longitude != null;
}

function gpsHelpMessage() {
  return "Turn ON Location. Allow GPS. Tap again.";
}

function deviceInfo() {
  return [navigator.userAgent, navigator.platform].filter(Boolean).join(" | ");
}
